"use client";

import * as React from "react";
import { usePathname } from "next/navigation";
import { BibliotekaIcon, PieChartIcon, UsersIcon, FaQIcon } from "@/components/icons";
import { HelpCircle } from "lucide-react";
import { useTranslations } from "next-intl";
import { useAppSelector } from "@/store";
import { isFaqManagerEnabled } from "@/lib/branding";
import { USER_ROLES, UserRole } from "@/src/lib/auth";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { NavUser } from "./NavUser";
import { NavMain } from "./NavMain";
import { NavExtras } from "./NavExtras";
import { NavSupport } from "./NavSupport";
import { Logo } from "./Logo";

type NavConfigItem = {
  key: string;
  titleKey: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  href: string;
  roles: ReadonlyArray<UserRole>;
};

// Full manager roles. Teacher is intentionally excluded: a teacher has
// proctor-level access and only sees the "Exams" section.
const MANAGER_ROLES = [
  USER_ROLES.REVIEWER,
  USER_ROLES.MANAGER,
  USER_ROLES.ADMIN,
] as const;

const NAV_CONFIG: ReadonlyArray<NavConfigItem> = [
  {
    key: "assignments",
    titleKey: "label-assignments",
    icon: BibliotekaIcon,
    href: "/assignments",
    roles: [
      USER_ROLES.STUDENT,
      USER_ROLES.PROCTOR,
      USER_ROLES.TEACHER,
      ...MANAGER_ROLES,
    ],
  },
  {
    key: "users",
    titleKey: "label-users",
    icon: UsersIcon,
    href: "/groups",
    roles: MANAGER_ROLES,
  },
  {
    key: "analytics",
    titleKey: "label-analytics",
    icon: PieChartIcon,
    href: "/analytics",
    roles:  [
      USER_ROLES.PROCTOR,
      ...MANAGER_ROLES,
    ],
  },
  {
    key: "faq",
    titleKey: "label-faq",
    icon: FaQIcon,
    href: "/faq",
    roles: MANAGER_ROLES,
  },
];

function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const t = useTranslations();
  const pathname = usePathname();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { user } = useAppSelector((s) => s.auth);
  const faqEnabled = isFaqManagerEnabled();

  const role = user?.group as UserRole | undefined;

  const sections = NAV_CONFIG
    .filter(
      (item) =>
        role &&
        item.roles.includes(role) &&
        (item.key !== "faq" || faqEnabled)
    )
    .map((item) => ({
      title: t(item.titleKey),
      icon: item.icon,
      href: item.href,
      isActive: pathname.startsWith(item.href),
      is_coming_soon: false,
    }));


  return (
    <Sidebar
      className="data-[collapsible=icon]:w-16 !min-w-16 group-data-[side=left]:border-r-0"
      collapsible="icon"
      {...props}
    >
      <SidebarHeader className="border-b-[1.5px] border-[rgba(255,255,255,0.75)] h-16 flex items-start justify-center px-4">
        {user?.school_id === 822 ? (
          <img
            src={isCollapsed ? "/ai-ministry-mini.svg" : "/ai-ministry-dark.avif"}
            alt=""
            className={`${isCollapsed ? "h-7" : "h-10"} w-auto object-contain min-w-0`}
          />
        ) : (
          <Logo variant="dark" collapsed={isCollapsed} forceBrand />
        )}
      </SidebarHeader>

      <SidebarContent>
        <NavMain items={sections} />
      </SidebarContent>

      <NavExtras />
      <NavSupport />

      <SidebarFooter className="border-t-[1.5px] border-[rgba(255,255,255,0.75)] px-2 pt-3 pb-3">
        <NavUser user={user} />
      </SidebarFooter>
      <SidebarRail className="hover:after:bg-transparent" />
    </Sidebar>
  );
}
export default AppSidebar;
